import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Save, X, Plus, Trash2 } from 'lucide-react';
import type { WorkflowStage, NotificationConfig } from '@/types/dispute-types';
import { mockRoles } from '@/lib/mock-data/dispute-types-mock-data';

interface WorkflowStageEditorProps {
  stage: WorkflowStage | null;
  existingStages: WorkflowStage[];
  open: boolean;
  onClose: () => void;
  onSave: (stage: WorkflowStage) => void;
}

const stageColors = [
  { name: 'Blue', value: '#3B82F6' },
  { name: 'Purple', value: '#8B5CF6' },
  { name: 'Amber', value: '#F59E0B' },
  { name: 'Green', value: '#10B981' },
  { name: 'Red', value: '#EF4444' },
  { name: 'Pink', value: '#EC4899' },
  { name: 'Cyan', value: '#06B6D4' },
  { name: 'Indigo', value: '#6366F1' },
];

export function WorkflowStageEditor({ stage, existingStages, open, onClose, onSave }: WorkflowStageEditorProps) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3B82F6',
    requiresApproval: false,
    approverRoles: [] as string[],
    slaHours: 24,
    slaWarningHours: 20,
    autoEscalate: false,
    escalationRoles: [] as string[],
    allowReassignment: true,
    allowedAssigneeRoles: [] as string[],
  });

  useEffect(() => {
    if (stage) {
      setFormData({
        name: stage.name,
        description: stage.description,
        color: stage.color,
        requiresApproval: stage.properties.requiresApproval,
        approverRoles: stage.properties.approverRoles || [],
        slaHours: stage.properties.slaHours || 24,
        slaWarningHours: stage.properties.slaWarningHours || 20,
        autoEscalate: stage.properties.autoEscalate,
        escalationRoles: stage.properties.escalationRoles || [],
        allowReassignment: stage.properties.allowReassignment,
        allowedAssigneeRoles: stage.properties.allowedAssigneeRoles || [],
      });
    } else {
      setFormData({
        name: '',
        description: '',
        color: '#3B82F6',
        requiresApproval: false,
        approverRoles: [],
        slaHours: 24,
        slaWarningHours: 20,
        autoEscalate: false,
        escalationRoles: [],
        allowReassignment: true,
        allowedAssigneeRoles: [],
      });
    }
  }, [stage, open]);

  const handleSave = () => {
    const savedStage: WorkflowStage = {
      id: stage?.id || '',
      name: formData.name,
      description: formData.description,
      order: stage?.order || 0,
      color: formData.color,
      properties: {
        requiresApproval: formData.requiresApproval,
        approverRoles: formData.approverRoles,
        slaHours: formData.slaHours,
        slaWarningHours: formData.slaWarningHours,
        autoEscalate: formData.autoEscalate,
        escalationRoles: formData.escalationRoles,
        allowReassignment: formData.allowReassignment,
        allowedAssigneeRoles: formData.allowedAssigneeRoles,
      },
      notifications: stage?.notifications || [],
    };

    onSave(savedStage);
  };

  const toggleRole = (roleId: string, field: 'approverRoles' | 'escalationRoles' | 'allowedAssigneeRoles') => {
    const roles = formData[field];
    if (roles.includes(roleId)) {
      setFormData({ ...formData, [field]: roles.filter(r => r !== roleId) });
    } else {
      setFormData({ ...formData, [field]: [...roles, roleId] });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{stage ? 'Edit Workflow Stage' : 'Add Workflow Stage'}</DialogTitle>
          <DialogDescription>
            Configure the stage properties, SLA requirements, and assignment rules
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4">
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold">Basic Information</h3>
              
              <div className="space-y-2">
                <Label htmlFor="stage-name">Stage Name *</Label>
                <Input
                  id="stage-name"
                  placeholder="e.g., Under Investigation"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="stage-description">Description *</Label>
                <Textarea
                  id="stage-description"
                  placeholder="Describe what happens in this stage..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label>Stage Color</Label>
                <div className="flex flex-wrap gap-2">
                  {stageColors.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, color: color.value })}
                      className={`w-10 h-10 rounded-lg border-2 transition-all ${
                        formData.color === color.value ? 'border-primary scale-110' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Approval Settings */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold">Approval Settings</h3>
              
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-0.5">
                  <Label>Requires Approval</Label>
                  <p className="text-sm text-muted-foreground">
                    Require approval before moving to next stage
                  </p>
                </div>
                <Switch
                  checked={formData.requiresApproval}
                  onCheckedChange={(checked) => setFormData({ ...formData, requiresApproval: checked })}
                />
              </div>

              {formData.requiresApproval && (
                <div className="space-y-2">
                  <Label>Approver Roles</Label>
                  <div className="space-y-2">
                    {mockRoles.map((role) => (
                      <div key={role.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`approver-${role.id}`}
                          checked={formData.approverRoles.includes(role.name)}
                          onCheckedChange={() => toggleRole(role.name, 'approverRoles')}
                        />
                        <Label htmlFor={`approver-${role.id}`} className="cursor-pointer">
                          {role.name}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* SLA Configuration */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold">SLA Configuration</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sla-hours">SLA Hours</Label>
                  <Input
                    id="sla-hours"
                    type="number"
                    value={formData.slaHours}
                    onChange={(e) => setFormData({ ...formData, slaHours: parseInt(e.target.value) || 0 })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sla-warning">Warning Hours</Label>
                  <Input
                    id="sla-warning"
                    type="number"
                    value={formData.slaWarningHours}
                    onChange={(e) => setFormData({ ...formData, slaWarningHours: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-0.5">
                  <Label>Auto-Escalate on SLA Breach</Label>
                  <p className="text-sm text-muted-foreground">
                    Automatically escalate when SLA is breached
                  </p>
                </div>
                <Switch
                  checked={formData.autoEscalate}
                  onCheckedChange={(checked) => setFormData({ ...formData, autoEscalate: checked })}
                />
              </div>

              {formData.autoEscalate && (
                <div className="space-y-2">
                  <Label>Escalation Roles</Label>
                  <div className="space-y-2">
                    {mockRoles.map((role) => (
                      <div key={role.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`escalation-${role.id}`}
                          checked={formData.escalationRoles.includes(role.name)}
                          onCheckedChange={() => toggleRole(role.name, 'escalationRoles')}
                        />
                        <Label htmlFor={`escalation-${role.id}`} className="cursor-pointer">
                          {role.name}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Assignment Rules */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold">Assignment Rules</h3>
              
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-0.5">
                  <Label>Allow Reassignment</Label>
                  <p className="text-sm text-muted-foreground">
                    Allow disputes to be reassigned in this stage
                  </p>
                </div>
                <Switch
                  checked={formData.allowReassignment}
                  onCheckedChange={(checked) => setFormData({ ...formData, allowReassignment: checked })}
                />
              </div>

              {formData.allowReassignment && (
                <div className="space-y-2">
                  <Label>Allowed Assignee Roles</Label>
                  <div className="space-y-2">
                    {mockRoles.map((role) => (
                      <div key={role.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`assignee-${role.id}`}
                          checked={formData.allowedAssigneeRoles.includes(role.name)}
                          onCheckedChange={() => toggleRole(role.name, 'allowedAssigneeRoles')}
                        />
                        <Label htmlFor={`assignee-${role.id}`} className="cursor-pointer">
                          {role.name}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </ScrollArea>

        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={onClose}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!formData.name || !formData.description}>
            <Save className="h-4 w-4 mr-2" />
            Save Stage
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
